/**
 * ActionRejectModal - 拒绝动作确认弹窗
 * 
 * 功能：
 * 1. 确认拒绝动作
 * 2. 可选填写拒绝原因（用于后续分析优化）
 * 3. 提供常用原因快捷选择
 */
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, XCircle, AlertTriangle, Loader2, MessageSquare } from 'lucide-react'
import { clsx } from 'clsx'

interface ActionRejectModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason?: string) => Promise<void>
  actionContent: string
  actionGrade: 'A' | 'B' | 'C'
}

// 常用拒绝原因
const COMMON_REASONS = [
  { id: 'timing', label: '时机不合适', description: '当前阶段不适合执行此动作' },
  { id: 'resource', label: '资源不足', description: '没有足够的时间或人力执行' },
  { id: 'alternative', label: '已有替代方案', description: '已通过其他方式解决' },
  { id: 'customer', label: '客户不配合', description: '客户端条件不满足' },
  { id: 'irrelevant', label: '不相关', description: '此动作与当前情况不匹配' },
]

// 等级配置
const GRADE_CONFIG = {
  A: { label: '最佳路径', color: 'text-green-600', bgColor: 'bg-green-100' },
  B: { label: '快速验证', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  C: { label: '止血动作', color: 'text-amber-600', bgColor: 'bg-amber-100' },
}

export default function ActionRejectModal({
  isOpen,
  onClose,
  onConfirm,
  actionContent,
  actionGrade,
}: ActionRejectModalProps) {
  const [selectedReason, setSelectedReason] = useState<string | null>(null)
  const [customReason, setCustomReason] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const gradeConfig = GRADE_CONFIG[actionGrade] || GRADE_CONFIG.B

  const handleConfirm = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const reason = selectedReason === 'custom' 
        ? customReason 
        : COMMON_REASONS.find(r => r.id === selectedReason)?.label
      await onConfirm(reason)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : '操作失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setSelectedReason(null)
      setCustomReason('')
      setError(null)
      onClose()
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 背景遮罩 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* 弹窗内容 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
              {/* 头部 */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  <h3 className="font-semibold text-gray-900">拒绝动作</h3>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isLoading}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* 内容 */}
              <div className="p-6 space-y-4">
                {/* 动作内容 */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={clsx(
                      'text-xs font-bold px-2 py-0.5 rounded-full',
                      gradeConfig.bgColor, gradeConfig.color
                    )}>
                      {actionGrade}级 · {gradeConfig.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 line-clamp-3">
                    {actionContent}
                  </p>
                </div>

                {/* 拒绝原因选择 */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-gray-400" />
                    拒绝原因
                    <span className="text-xs text-gray-400 font-normal">（可选）</span>
                  </label>

                  {/* 常用原因 */}
                  <div className="space-y-2">
                    {COMMON_REASONS.map((reason) => (
                      <button
                        key={reason.id}
                        onClick={() => setSelectedReason(reason.id)}
                        className={clsx(
                          'w-full px-4 py-3 text-left rounded-lg border-2 transition-all',
                          selectedReason === reason.id
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                        )}
                      >
                        <div className="font-medium text-sm text-gray-900">
                          {reason.label}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {reason.description}
                        </div>
                      </button>
                    ))}

                    {/* 自定义原因 */}
                    <button
                      onClick={() => setSelectedReason('custom')}
                      className={clsx(
                        'w-full px-4 py-3 text-left rounded-lg border-2 transition-all',
                        selectedReason === 'custom'
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                      )}
                    >
                      <div className="font-medium text-sm text-gray-900">
                        其他原因
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        填写自定义原因
                      </div>
                    </button>
                  </div>

                  {/* 自定义原因输入框 */}
                  {selectedReason === 'custom' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <textarea
                        value={customReason}
                        onChange={(e) => setCustomReason(e.target.value)}
                        placeholder="请输入拒绝原因..."
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                      />
                    </motion.div>
                  )}
                </div>

                {/* 提示 */}
                <div className="flex items-start gap-2 text-xs text-gray-500">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-500" />
                  <span>
                    拒绝后此动作将不再显示。填写原因可帮助系统优化推荐质量。
                  </span>
                </div>

                {/* 错误提示 */}
                {error && (
                  <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
                    {error}
                  </div>
                )}
              </div>

              {/* 底部按钮 */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={handleClose}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      处理中...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      确认拒绝
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
